import type { AtlasCatalogItem } from '../services/atlasService';

export function getAtlasFamily(atlas: AtlasCatalogItem): string {
  if (atlas.family) return atlas.family;
  const identity = `${atlas.id} ${atlas.name}`;
  if (/PD126/i.test(identity)) return 'PD126';
  if (/PD25/i.test(identity)) return 'PD25';
  if (/massp/i.test(identity)) return 'MASSP2.0';
  return /morel/i.test(identity) ? 'Morel' : 'MNI';
}

export function getAtlasFamilyTitle(family: string): string {
  if (family === 'MNI') return 'ICBM 2009b NLIN ASYM';
  return family === 'MASSP2.0' ? 'MASSP 2.0' : family;
}

export function getAtlasDisplayName(atlas: AtlasCatalogItem): string {
  if (getAtlasFamily(atlas) !== 'MNI') return atlas.name;
  return [
    getAtlasFamilyTitle('MNI'),
    atlas.contrast,
    atlas.resolution_mm != null ? `${atlas.resolution_mm} mm` : null,
  ].filter(Boolean).join(' · ');
}

/** Keep the contrast visible when a narrow viewer dropdown truncates the name. */
export function getAtlasOptionLabel(atlas: AtlasCatalogItem): string {
  if (!atlas.contrast) return getAtlasDisplayName(atlas);
  return [
    atlas.contrast,
    getAtlasFamilyTitle(getAtlasFamily(atlas)),
    atlas.resolution_mm != null ? `${atlas.resolution_mm} mm` : null,
  ].filter(Boolean).join(' · ');
}

export function getSelectedAtlasChoices(atlases: AtlasCatalogItem[], selected?: AtlasCatalogItem) {
  if (!selected) return [];
  const choices = selectAtlasTemplates(atlases.filter(atlas => getAtlasFamily(atlas) === getAtlasFamily(selected)));
  return choices.some(atlas => atlas.id === selected.id) ? choices : [...choices, selected];
}

/** Compact viewing defaults, not a clinical ranking. Keep full-brain 1 mm
 * templates ahead of the larger (sometimes regional) high-resolution data.
 * The complete catalog remains available for already-open viewer sources.
 */
export default function selectAtlasTemplates(atlases: AtlasCatalogItem[]): AtlasCatalogItem[] {
  // Non-anatomical atlases have explicit overview maps, not T1/T2 contrasts.
  const maps = atlases.filter(atlas => atlas.data_kind === 'labelmap' || atlas.data_kind === 'probability');
  if (maps.length) return maps;
  if (!atlases.some(atlas => atlas.contrast)) return atlases;

  const resolutionRank = (atlas: AtlasCatalogItem) =>
    atlas.resolution_mm == null ? Infinity : Math.abs(atlas.resolution_mm - 1);
  const choose = (contrasts: string[]) =>
    atlases
      .filter(atlas => contrasts.includes(atlas.contrast ?? ''))
      .sort(
        (a, b) =>
          resolutionRank(a) - resolutionRank(b) ||
          contrasts.indexOf(a.contrast!) - contrasts.indexOf(b.contrast!) ||
          b.id.localeCompare(a.id)
      )[0];

  return [
    choose(['T1w MPRAGE', 'T1w GRE', 'T1w']),
    choose(['T2w', 'T2*w']),
    choose(['CT']),
  ].filter((atlas): atlas is AtlasCatalogItem => Boolean(atlas));
}
